import axios from "axios";
import { TokenManager } from "./auth";
import { isFunction } from "lodash";

/**
 * Create an axios instance with basic configuration
 * @param {object} options - Configuration options
 * @param {string} options.baseUrl - Base URL for API calls
 * @param {boolean} options.isAdminSite - Admin sites append "/admin" to the base URL+api/
 * @param {function} options.onAuthError - Callback when auth error occurs
 * @returns {import('axios').AxiosInstance} Configured axios instance
 */
export const createClient = (options = {}) => {
  const {
    baseUrl: _baseUrl = options?.baseUrl ||
      process.env?.API_URL ||
      process.env?.REACT_APP_BACKEND_URL ||
      "http://localhost:8069",
    timeout = 30000,
    headers = {},
    isAdminSite = false,
    withCredentials = true,
  } = options;

  const baseUrl = _baseUrl.replace(/\/$/, "");

  // Create instance with basic configuration
  //console.log(`[utils] createClient(): ${baseUrl}`, options);
  const client = axios.create({
    baseURL: `${baseUrl}/api${isAdminSite ? "/admin" : ""}`,
    timeout,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...headers,
    },
    withCredentials, // Send cookies with requests?
  });

  addInterceptors(client, options);
  return client;
};

/**
 * Add request logging interceptors to an axios instance
 * @param {import('axios').AxiosInstance} client - Axios instance
 * @param {Object} options - Configuration options
 * @returns {import('axios').AxiosInstance} The same axios instance with interceptors
 * @see {@linkcode createClient}
 * @see {@link https://axios-http.com/docs/interceptors}
 */
export const addInterceptors = (client, options = {}) => {
  const { isAdminSite = false, onAuthError = () => {} } = options;

  // Request interceptor with token handling
  client.interceptors.request.use(
    (config) => {
      // Get token for auth header - API may need both cookie and auth header
      const token = TokenManager.getToken();
      console.log("[utils] addInterceptors() config: ", config);
      if (config.url && config.url.startsWith("/api/user")) {
        // For /api/user endpoints, use absolute URL to avoid baseURL prefix
        // Extract the URL from baseURL up to /api/ and append the full /api/user path
        const apiBaseUrl = config.baseURL.replace(/\/api\/?$/, "");
        config.baseURL = apiBaseUrl;
        console.log(
          `[utils] ⚠️ API endpoint URL fixing: ${config.url} > ${config.baseURL}${config.url}`,
        );
        // Ensure we're using HTTPS for production
        if (
          config.baseURL.startsWith("http://") &&
          !config.baseURL.includes("localhost") &&
          !config.baseURL.includes("127.0.0.1")
        ) {
          config.baseURL = config.baseURL.replace("http://", "https://");
          console.log(
            `[utils] 🔒 Forced HTTPS for production: ${config.baseURL}`,
          );
        }
      }

      // Log request information
      log.debug(
        `[utils] ${isAdminSite ? "[Admin]" : ""} API Req: ${config.method} ${config.url}`,
      );

      // Only set Authorization header if we have a token
      // The cookie will be sent automatically due to withCredentials
      if (token) {
        // Ensure authorization header is properly set and formatted
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.log(`[utils] ⚠️ No token available for request: ${config.url}`);
      }

      // Remove Content-Type header for FormData to allow proper file upload
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }

      return config;
    },
    (error) => {
      console.error("[utils] ❌ Request interceptor error:", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor with improved error handling
  client.interceptors.response.use(
    (response) => {
      console.log(
        `[utils] ✅ Response [${response.status}] ${response.config.url}`,
        {
          statusText: response.statusText,
        },
      );
      return response;
    },
    (error) => {
      console.error(
        `[utils] ❌ API Error [${error.response?.status}]: ${error.config?.url}`,
        {
          message: error.response?.data?.message || error.message,
        },
      );

      // Show comprehensive auth error info
      if (error.response?.status === 401) {
        log.warn("[utils] Authentication failure:", {
          url: error.config?.url,
          responseMessage: error.response?.data?.message,
        });
        if (!error.config?.url?.includes("/login")) {
          TokenManager.clearToken();
        }
        if (isFunction(onAuthError) && onAuthError) {
          onAuthError(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
};

/**
 * Create a fully configured client with all basic interceptors
 * @param {Object} options - Configuration options
 * @returns {import('axios').AxiosInstance} Configured axios instance
 * @see {@linkcode createClient}
 */
export const createConfiguredClient = (options = {}) => createClient(options);

// Pre-configured clients for convenience
export const client = createClient();
export const authedClient = createClient({
  withCredentials: true,
});
export const publicClient = createClient({ withCredentials: false });
